<template>
  <v-container fluid fill-height ma-0 pa-1 >
    <v-layout column fill-height justify-start>
      <v-flex xs12 id="g_location_container_wrapper" style="position: inherit; overflow: hidden;">
        <l-graph
          v-if="showGraph"
          :data="getData"
          :devices="devices"
          :visConnections="getConnections"
          @select="(payload) => onRowOpen(payload)"
        />
      </v-flex>

      <v-expansion-panel
          light
          expand
          flat
          v-model="tablePanel.internal.open"
          class="my-1 px-1"
        >
          <v-expansion-panel-content>
                <template v-slot:header>
                  <v-layout justify-start>
                    <span class="body-2 text-uppercase">{{$t('internal connections')}}</span>
                  </v-layout>
                </template>

                  <v-card flat height="420px"  >
                    <scroll-table
                      :data="getInternalTable"
                      :options="options"
                      :sortable="false"
                      :subscribed="false"
                      :sorted="internalSort"
                      @select="(row) => onRowOpen(row)"
                      @sort="(item) => sortInternalTable(item)"
                    />
                  </v-card>

          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel
          light
          expand
          flat
          v-model="tablePanel.outer.open"
          class="my-1 px-1"
        >
          <v-expansion-panel-content>
                <template v-slot:header>
                  <v-layout justify-start>
                    <span class="body-2 text-uppercase">{{$t('outer connections')}}</span>
                  </v-layout>
                </template>

                  <v-card flat height="420px"  >
                    <scroll-table
                      :data="getOuterTable"
                      :options="options"
                      :sortable="false"
                      :subscribed="false"
                      :sorted="outerSort"
                      @select="(row) => onRowOpen(row)"
                      @sort="(item) => sortOuterTable(item)"
                    />
                  </v-card>

          </v-expansion-panel-content>
        </v-expansion-panel>
    </v-layout>
  </v-container>
</template>

<script lang="ts">
import Vue from 'vue'
import loadingCircle from '@/nms/components/partials/loader.vue'
import scrollTable from '@/nms/components/partials/scrolltable.vue'
import LGraph from '@/nms/pages/topology/g-location.vue'

// Типы
interface EndpointRef {
  $id?: string
  ref?: string
  namePath?: string[]
  path?: Array<{ cls?: string; [key: string]: any }>
  [key: string]: any
}

interface ConnectionItem {
  $id?: string
  id?: string
  name?: string
  template?: string
  cls?: string
  refs?: EndpointRef[]
  brefs?: Array<{ ref?: string; name?: string; type?: string; [key: string]: any }>
  points?: EndpointRef[]
  point_a?: Array<{ path: string; obj: EndpointRef; device?: any }>
  point_b?: Array<{ path: string; obj: EndpointRef; device?: any }>
  services?: string[]
  isInternal?: boolean
  [key: string]: any
}

interface TableColumn {
  title: string
  value: string
  type: string
  width: string
}

interface TableData {
  columns: TableColumn[]
  rows: ConnectionItem[]
}

interface OptionsColumns {
  [key: string]: { width: string }
}

interface OptionsData {
  columns: OptionsColumns
}

interface TablePanelState {
  internal: { open: boolean[] }
  outer: { open: boolean[] }
}

interface DeviceData {
  $id?: string
  [key: string]: any
}

interface ActiveTreeItem {
  $id?: string
  [key: string]: any
}

export default Vue.extend({
  name: 'LOCATION_CONNECTIONS',

  components: {
    loadingCircle,
    scrollTable,
    LGraph
  },

  props: {
    data: {
      type: Object as () => DeviceData,
      default: () => ({})
    },
    devices: {
      type: Array as () => any[],
      default: () => []
    },
  },

  data(): {
    connections: ConnectionItem[]
    showGraph: boolean
    options: OptionsData
    table: TableData
    tablePanel: TablePanelState
    filter: any | null
    search: string
    internalSort: string
    outerSort: string
  } {
    return {
      connections: [],
      showGraph: false,
      options: {
        columns: {
          text: { width: '40%' },
          timestamp: { width: '40%' },
          actions: { width: '40%' },
          endpoint: { width: '40%' },
          check: { width: '20%' },
        },
      },
      table: {
        columns: [],
        rows: []
      },
      tablePanel: {
        internal: { open: [false] },
        outer: { open: [false] },
      },
      filter: null,
      search: '',
      internalSort: '',
      outerSort: '',
    }
  },

  computed: {
    getInternalTable(): TableData {
      const t = this.$merge({}, this.table) as TableData
      t.rows = t.rows.filter(row => row.isInternal)
      
      if (this.internalSort) {
        const isDescending = this.internalSort.charAt(0) === '-'
        const fieldName = isDescending ? this.internalSort.slice(1) : this.internalSort
        const direction = isDescending ? -1 : 1
        t.rows = [...t.rows].sort(this.$compare(fieldName, direction))
      }
      return t
    },

    getOuterTable(): TableData {
      const t = this.$merge({}, this.table) as TableData
      t.rows = t.rows.filter(row => !row.isInternal)
      
      if (this.outerSort) {
        const isDescending = this.outerSort.charAt(0) === '-'
        const fieldName = isDescending ? this.outerSort.slice(1) : this.outerSort
        const direction = isDescending ? -1 : 1
        t.rows = [...t.rows].sort(this.$compare(fieldName, direction))
      }
      return t
    },

    getData(): DeviceData {
      return this.data
    },

    getConnections(): ConnectionItem[] {
      return this.connections
    },
  },

  watch: {
    '$store.state.devices.treeActive': {
      handler(newVal: ActiveTreeItem[]): void {
        console.log(this.$options.name, 'WATCH', '$store.state.devices.treeActive', newVal)
        if (!newVal) return
        this.setup()
      },
      deep: true
    },
  },

  created(): void {
    this.setup()
  },

  methods: {
    async setup(): Promise<void> {
      this.reset()
      this.statusUnsubscribe('LOCATION_CONNECTIONS')
      await this.loadConnections()
      this.showGraph = true
    },

    async loadConnections(): Promise<void> {
      try {
        this.connections = []
        const activeTree = this.$store.state.devices.treeActive as ActiveTreeItem[]
        const obj$ = await this.$bridge.getObjectById(activeTree[0]?.$id)
        
        const query = {
          id: [
            {
              fnc: 'getObjectConnections',
              data: { id: obj$.$id }
            }
          ],
          notNull: true,
          namePath: true,
          data: {
            parent: false,
            namePath: true,
            refs: {
              data: { namePath: true, path: true }
            },
            brefs: {
              data: { namePath: true, path: true }
            },
          }
        }
        
        const connections = await this.$bridge.cmdbQuery(query)
        console.log(this.$options.name, 'loadConnections', 'connections', connections)
        this.connections = connections || []
        this.prepareColumns()
      } catch (err) {
        console.error(this.$options.name, 'loadConnections', 'smth wrong', err)
      }
    },

    async prepareColumns(): Promise<void> {
      this.table = {
        columns: [
          {
            title: 'name',
            value: 'name',
            type: 'text',
            width: '20%'
          },
          {
            title: 'type',
            value: 'template',
            type: 'text',
            width: '10%'
          },
          {
            title: 'point_a',
            value: 'point_a',
            type: 'endpoint',
            width: '20%'
          },
          {
            title: 'point_b',
            value: 'point_b',
            type: 'endpoint',
            width: '20%'
          },
          {
            title: 'services',
            value: 'services',
            type: 'array',
            width: '30%'
          },
        ],
        rows: []
      }
      this.prepareRows()
    },

    async prepareRows(): Promise<void> {
      this.table.rows = this.connections.map(connection => {
        if (connection.refs?.length) {
          connection.points = []
          connection.refs.forEach(ref => {
            if (ref.ref === 'endpoint') {
              connection.points?.push(ref)
            }
          })
          console.log(this.$options.name, 'prepareRows', 'points', connection.points)
          
          if (connection.points?.length) {
            const point = connection.points[0].namePath
            connection.point_a = [{
              path: point?.slice(10) || '',
              obj: connection.points[0]
            }]
            
            if (connection.points[0].path?.length) {
              const parentDevice = connection.points[0].path.find(o => o.cls === 'device')
              connection.point_a[0].device = parentDevice
            }
            
            if (connection.points[1]) {
              const point = connection.points[1].namePath
              connection.point_b = [{
                path: point?.slice(10) || '',
                obj: connection.points[1]
              }]
              
              if (connection.points[1].path?.length) {
                const parentDevice = connection.points[1].path.find(o => o.cls === 'device')
                connection.point_b[0].device = parentDevice
              }
            }
            this.statusSubscribe(connection.points, 'LOCATION_CONNECTIONS')
          }
        }

        if (connection.brefs?.length) {
          connection.services = []
          connection.brefs.forEach(bref => {
            if (bref.ref === 'connection') {
              connection.services?.push(`${bref.name} (${bref.type})`)
            }
          })
        }
        return connection
      })
      this.statusSubscribe(this.connections, 'LOCATION_CONNECTIONS')
      console.log(this.$options.name, 'prepareRows', 'table', this.table)
      console.log(this.$options.name, 'prepareRows', 'this.connections', this.connections)
    },

    async onRowOpen(obj: ConnectionItem): Promise<void> {
      console.log(this.$options.name, 'onRowOpen', 'obj', obj)
      
      if (obj.cls === 'device') {
        const query = {
          id: [obj.$id],
          notNull: true,
          data: { links: { flatten: false } }
        }

        const obj$ = await this.$bridge.cmdbQuery(query)
        if (!obj$?.length) return
        
        obj$[0].children = obj$[0].links
        if (obj$[0].cls === 'device') {
          await this.$store.dispatch('activeDevice', obj$[0])
        }
      }

      if (obj.cls === 'connection') {
        const dialogData = {
          component: 'journalModal',
          id: obj.$id,
          data: obj,
          options: {
            width: (this.$el as HTMLElement).clientWidth * 0.8,
            height: (this.$el as HTMLElement).clientHeight * 0.7,
            top: 100
          }
        }
        this.$store.dispatch('setDialog', dialogData)
      }
    },

    async statusSubscribe(arr: any[], group: string): Promise<void> {
      try {
        this.$store.dispatch('measuresSubscribeInit', {
          name: 'addStatus',
          group: 'LOCATION_CONNECTIONS',
          listener: 'status'
        })
        
        if (arr.length) {
          arr.forEach(item => {
            if (item.$id) {
              this.$store.dispatch('addStatusKey', item)
            }
          })
          await this.$bridge.subscribe(arr.map(obj => obj.$id))
        }
      } catch (error) {
        console.error(this.$options.name, 'statusSubscribe', 'error', error)
      }
    },

    subscribeHandler(e: { $id?: string; [key: string]: any }): void {
      if (e.$id) {
        this.$store.dispatch('addStatus', e)
      }
    },

    statusUnsubscribe(group: string): void {
      this.$store.dispatch('measuresSubscribeClose', {
        handler: this.subscribeHandler,
        group: 'LOCATION_CONNECTIONS',
        listener: 'status'
      })
    },

    sortInternalTable(item: TableColumn): void {
      if (this.internalSort === item.value) {
        this.internalSort = '-' + item.value
      } else {
        this.internalSort = item.value
      }
    },

    sortOuterTable(item: TableColumn): void {
      if (this.outerSort === item.value) {
        this.outerSort = '-' + item.value
      } else {
        this.outerSort = item.value
      }
    },

    reset(): void {
      this.connections = []
      this.showGraph = false
      this.table = {
        columns: [],
        rows: []
      }
    },

    setLoader(name: string, status: boolean): void {
      this.$store.dispatch('setLoader', {
        key: name,
        value: status
      })
    },
  },
})
</script>

<style>
</style>
